import { useEffect, useState } from 'react';
import { FRAME_OPTIONS, formatUsd } from '@lumina/shared';
import { api, API_BASE } from '../lib/api';
import { CopyableId } from './ExternalLinks';
import { formatDate } from '../lib/format';

// Every stored file for a photo, rendered, plus the photo as customers see it
// in each storefront context. Driven by the editor's current state, so a fresh
// upload previews before the photo is saved.

export interface RenditionSource {
  src?: string;
  thumb_src?: string | null;
  original_key?: string | null;
  display_url?: string;
  thumb_url?: string;
  width?: number;
  height?: number;
  title?: string;
  photographer?: string;
  price_base_cents?: number;
  purged_at?: string | null;
}

interface ObjectMeta {
  bytes: number;
  content_type: string | null;
  uploaded: string;
}

interface FileRow {
  key: string;
  name: string;
  usedBy: string;
  /** URL to render/open; null for the private proxy. */
  url: string | null;
  isPrivate: boolean;
  /** Known dimensions (original); renditions are measured when they load. */
  dims?: { width: number; height: number };
}

const isUrl = (s?: string | null) => !!s && /^https?:\/\//i.test(s);

function fileRows(p: RenditionSource): FileRow[] {
  const rows: FileRow[] = [];
  // Purged: `src` is the kept thumbnail and nothing else remains.
  if (p.purged_at) {
    const key = p.thumb_src || p.src;
    return key
      ? [{ key, name: 'Thumbnail — watermarked', usedBy: 'Order history (photo purged)', url: p.thumb_url || null, isPrivate: false }]
      : [];
  }
  const legacy = !!p.src && !p.original_key;
  if (p.src) {
    rows.push({
      key: p.src,
      name: legacy ? 'Legacy master (public)' : 'Display — watermarked',
      usedBy: legacy
        ? 'Everything, including printing — not yet split into a private original'
        : 'Storefront gallery, product view and cart · Stripe checkout · order emails',
      url: p.display_url || (isUrl(p.src) ? p.src : null),
      isPrivate: false,
    });
  }
  if (p.thumb_src) {
    rows.push({
      key: p.thumb_src,
      name: 'Thumbnail — watermarked',
      usedBy: 'Admin photo list and print-size matrix',
      url: p.thumb_url || null,
      isPrivate: false,
    });
  }
  if (p.original_key) {
    rows.push({
      key: p.original_key,
      name: 'Original — private',
      usedBy: 'Gelato printing only (signed, expiring link) · never public',
      url: `${API_BASE}/api/admin/photos/master/${p.original_key}`,
      isPrivate: true,
      dims: p.width && p.height ? { width: p.width, height: p.height } : undefined,
    });
    rows.push({
      key: p.original_key.replace(/\/original\.[a-z0-9]+$/i, '/proxy.jpg'),
      name: 'Proxy — private',
      usedBy: 'Source for rebuilding previews when the original is over 20 MB',
      url: null,
      isPrivate: true,
    });
  }
  return rows;
}

export function RenditionsPanel({ photo }: { photo: RenditionSource }) {
  const rows = fileRows(photo);
  const [meta, setMeta] = useState<Record<string, ObjectMeta | null>>({});
  const r2Keys = rows.map((r) => r.key).filter((k) => !isUrl(k));
  const keySig = r2Keys.join('|');

  useEffect(() => {
    if (r2Keys.length === 0) return;
    api
      .post<{ objects: Record<string, ObjectMeta | null> }>('/api/admin/photos/renditions/inspect', { keys: r2Keys })
      .then((d) => setMeta(d.objects))
      .catch(() => setMeta({}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keySig]);

  // The proxy only exists for large masters — hide the row when R2 has nothing.
  const visible = rows.filter((r) => !(r.name.startsWith('Proxy') && meta[r.key] === null));
  const display = photo.display_url || (isUrl(photo.src) ? photo.src : undefined);

  if (!photo.src) {
    return (
      <Card title="Previews">
        <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Upload a print master to generate previews.</p>
      </Card>
    );
  }

  return (
    <>
      <Card title="Stored files" subtitle="Everything kept for this photo in R2, rendered as-is.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {visible.map((r) => (
            <FileCard key={r.key} row={r} meta={meta[r.key]} isUrlKey={isUrl(r.key)} />
          ))}
        </div>
      </Card>

      {display && !photo.purged_at && (
        <Card title="As customers see it" subtitle="The display rendition in each place the storefront and checkout use it.">
          <ContextPreviews photo={photo} display={display} />
        </Card>
      )}
    </>
  );
}

function FileCard({ row, meta, isUrlKey }: { row: FileRow; meta: ObjectMeta | null | undefined; isUrlKey: boolean }) {
  const [natural, setNatural] = useState<{ width: number; height: number } | null>(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const dims = natural ?? row.dims;
  // Rendering a print master can mean a 100 MB image — only on request.
  const renderImage = row.url && (!row.isPrivate || showOriginal);

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 260px', gap: 16, alignItems: 'start' }}>
      <div
        style={{
          background: 'repeating-conic-gradient(#f3f4f6 0% 25%, #fff 0% 50%) 50% / 16px 16px',
          border: '1px solid #e5e5e5',
          borderRadius: 6,
          minHeight: 120,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        {renderImage ? (
          <img
            src={row.url!}
            alt={row.name}
            onLoad={(e) => setNatural({ width: e.currentTarget.naturalWidth, height: e.currentTarget.naturalHeight })}
            style={{ display: 'block', maxWidth: '100%', maxHeight: 520, objectFit: 'contain' }}
          />
        ) : row.url ? (
          <button type="button" onClick={() => setShowOriginal(true)} style={btn}>
            Render original here
          </button>
        ) : (
          <span style={{ fontSize: 12, color: '#9ca3af', padding: 24, textAlign: 'center' }}>
            Private working file — not rendered
          </span>
        )}
      </div>
      <div style={{ fontSize: 13 }}>
        <div style={{ fontWeight: 600, marginBottom: 2 }}>
          {row.isPrivate && <span title="Private">🔒 </span>}
          {row.name}
        </div>
        <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 8 }}>{row.usedBy}</div>
        <Meta label="Pixels" value={dims ? `${dims.width} × ${dims.height}` : '—'} />
        <Meta label="File size" value={meta ? formatBytes(meta.bytes) : isUrlKey ? 'external URL' : meta === null ? 'missing in R2' : '…'} />
        <Meta label="Type" value={meta?.content_type ?? '—'} />
        <Meta label="Stored" value={formatDate(meta?.uploaded)} />
        <div style={{ marginTop: 6 }}>
          <CopyableId value={row.key} />
        </div>
        {row.url && (
          <a href={row.url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 8, fontSize: 12, color: '#2563eb' }}>
            Open full size ↗
          </a>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Storefront contexts — mirror apps/storefront components' sizing and styling.
// ──────────────────────────────────────────────────────────────────────────────

const FRAME_RING: Record<string, string | null> = {
  none: null,
  classic_black: '#000',
  modern_white: '#f3f4f6',
  oak: '#8B5A2B',
};

function ContextPreviews({ photo, display }: { photo: RenditionSource; display: string }) {
  const [frameId, setFrameId] = useState('none');
  const aspect = photo.width && photo.height ? photo.width / photo.height : 3 / 2;
  const title = photo.title || 'Untitled';
  const ring = FRAME_RING[frameId];

  return (
    <div style={{ display: 'grid', gap: 24 }}>
      <Context label="Gallery tile" note="Gallery.tsx · rows ~400px tall, cropped to fill; title shows on hover (shown here at half size).">
        <div style={{ position: 'relative', height: 200, width: Math.round(200 * aspect), maxWidth: '100%', overflow: 'hidden', borderRadius: 2 }}>
          <img src={display} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          <div
            style={{
              position: 'absolute',
              inset: 'auto 0 0 0',
              padding: 14,
              background: 'linear-gradient(to top, rgba(0,0,0,.8), rgba(0,0,0,.4), transparent)',
            }}
          >
            <div style={{ color: '#fff', fontFamily: 'Georgia, serif', fontSize: 16 }}>{title}</div>
            <div style={{ color: 'rgba(255,255,255,.8)', fontSize: 11, letterSpacing: 1, textTransform: 'uppercase' }}>
              {photo.photographer || ''}
            </div>
          </div>
        </div>
      </Context>

      <Context label="Product view" note="ProductModal.tsx · framed options add a white mat and an 8px frame.">
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {FRAME_OPTIONS.map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => setFrameId(f.id)}
              style={{ ...btn, ...(frameId === f.id ? { background: '#1a1a1a', color: '#fff', borderColor: '#1a1a1a' } : {}) }}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div style={{ background: '#f3f4f6', padding: 32, display: 'flex', justifyContent: 'center', borderRadius: 6 }}>
          <div
            style={{
              padding: ring ? 16 : 0,
              background: ring ? '#fff' : 'transparent',
              boxShadow: `${ring ? `0 0 0 8px ${ring}, ` : ''}0 25px 50px -12px rgba(0,0,0,.25)`,
            }}
          >
            <img src={display} alt="" style={{ display: 'block', maxHeight: 360, maxWidth: '100%', width: 'auto' }} />
          </div>
        </div>
      </Context>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 24 }}>
        <Context label="Cart" note="CartSheet.tsx · 80px square crop.">
          <div style={{ display: 'flex', gap: 16, padding: 12, border: '1px solid #e5e5e5', borderRadius: 8, background: '#fafafa' }}>
            <div style={{ width: 80, height: 80, borderRadius: 6, overflow: 'hidden', background: '#e5e7eb', flexShrink: 0 }}>
              <img src={display} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
            </div>
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 500 }}>{title}</div>
              <div style={{ color: '#6b7280', fontSize: 12, marginTop: 4 }}>8" x 12" • Print Only</div>
              {photo.price_base_cents != null && (
                <div style={{ fontWeight: 500, marginTop: 10 }}>{formatUsd(photo.price_base_cents)}</div>
              )}
            </div>
          </div>
        </Context>

        <Context label="Stripe checkout & order email" note="Line-item image — Stripe and email clients show it small and square.">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: 12, border: '1px solid #e5e5e5', borderRadius: 8 }}>
            <img src={display} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 4, display: 'block' }} />
            <div style={{ fontSize: 13 }}>
              <div style={{ fontWeight: 500 }}>{title} — 8" x 12"</div>
              <div style={{ color: '#6b7280', fontSize: 12 }}>Print only · Qty 1</div>
            </div>
          </div>
        </Context>
      </div>
    </div>
  );
}

function Context({ label, note, children }: { label: string; note: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontSize: 13, fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 10px' }}>{note}</div>
      {children}
    </div>
  );
}

function Card({ title, subtitle, children }: { title: string; subtitle?: string; children: React.ReactNode }) {
  return (
    <section
      id={title === 'Stored files' || title === 'Previews' ? 'previews' : undefined}
      style={{ background: '#fff', padding: 24, border: '1px solid #e5e5e5', borderRadius: 8, marginTop: 24, scrollMarginTop: 16 }}
    >
      <h2 style={{ fontFamily: 'Georgia, serif', fontSize: 20, fontWeight: 400, margin: 0 }}>{title}</h2>
      {subtitle && <p style={{ fontSize: 12, color: '#6b7280', margin: '4px 0 16px' }}>{subtitle}</p>}
      {children}
    </section>
  );
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12, padding: '2px 0' }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      <span style={{ textAlign: 'right' }}>{value}</span>
    </div>
  );
}

function formatBytes(n: number) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

const btn: React.CSSProperties = {
  padding: '6px 12px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #d4d4d4',
  borderRadius: 6,
  fontSize: 12,
  cursor: 'pointer',
};
